<template>
  <div class="gallery-overlay" @click.self="handleClose">
    <div class="image-gallery">
      <!-- Close Button -->
      <button class="close-btn" data-testid="image-gallery-close" @click="handleClose" :title="t('Fechar galeria')">
        <i class="pi pi-times"></i>
      </button>

      <div v-if="loading" class="loading-state">
        <i class="pi pi-spinner pi-spin fa-3x"></i>
        <p>{{ t('Carregando imagens...') }}</p>
      </div>

      <div v-else-if="processedImages.length === 0" class="no-images">
        <i class="pi pi-image fa-3x"></i>
        <p>{{ emptyMessage || t('Nenhuma imagem encontrada') }}</p>
      </div>

      <div v-else class="gallery-content">
        <!-- Main Image Display -->
        <div class="main-image-container">
          <div class="image-info" v-if="currentImage">
            <span class="image-label" :class="currentImage.type">
              {{ currentImage.label }}
            </span>
            <span class="image-counter">
              {{ currentIndex + 1 }} / {{ processedImages.length }}
            </span>
          </div>

          <div class="image-wrapper" :class="{ 'fullscreen': isFullscreen }">
            <img
              v-if="currentImage"
              :src="currentImage.url"
              :alt="currentImage.label"
              class="main-image"
              :class="{ 'image-loaded': !imageLoading }"
              :style="{ transform: `scale(${zoomLevel})` }"
              @load="imageLoading = false"
              @error="handleImageError"
            />
            <div v-if="imageLoading" class="loading-overlay">
              <i class="pi pi-spinner pi-spin fa-2x"></i>
            </div>
          </div>

          <!-- Navigation Arrows -->
          <button
            v-if="processedImages.length > 1"
            class="nav-arrow nav-prev"
            data-testid="image-gallery-prev"
            @click="previousImage"
            :disabled="currentIndex === 0"
          >
            <i class="pi pi-chevron-left"></i>
          </button>
          <button
            v-if="processedImages.length > 1"
            class="nav-arrow nav-next"
            data-testid="image-gallery-next"
            @click="nextImage"
            :disabled="currentIndex === processedImages.length - 1"
          >
            <i class="pi pi-chevron-right"></i>
          </button>

          <!-- Toolbar -->
          <div class="toolbar">
            <button
              v-if="showZoomControls"
              class="tool-btn"
              data-testid="image-gallery-zoom-out"
              @click="zoomOut"
              :disabled="zoomLevel <= 0.5"
              :title="t('Reduzir zoom')"
            >
              <i class="pi pi-search-minus"></i>
            </button>
            <button
              v-if="showZoomControls"
              class="tool-btn"
              data-testid="image-gallery-zoom-reset"
              @click="resetZoom"
              :title="t('Resetar zoom')"
            >
              <i class="pi pi-search"></i> {{ Math.round(zoomLevel * 100) }}%
            </button>
            <button
              v-if="showZoomControls"
              class="tool-btn"
              data-testid="image-gallery-zoom-in"
              @click="zoomIn"
              :disabled="zoomLevel >= 3"
              :title="t('Aumentar zoom')"
            >
              <i class="pi pi-search-plus"></i>
            </button>
            <button
              v-if="showFullscreenControl"
              class="tool-btn"
              data-testid="image-gallery-fullscreen"
              @click="toggleFullscreen"
              :title="isFullscreen ? t('Sair tela cheia') : t('Tela cheia')"
            >
              <i :class="isFullscreen ? 'pi pi-compress' : 'pi pi-expand'"></i>
            </button>
            <button
              v-if="showDeleteButton && currentImage"
              class="tool-btn tool-btn-danger"
              data-testid="image-gallery-delete"
              @click="handleDeleteCurrent"
              :disabled="isDeleting"
              :title="t('Remover imagem')"
            >
              <i class="pi pi-trash"></i>
            </button>
          </div>
        </div>

        <!-- Thumbnail Strip -->
        <div class="thumbnail-strip" v-if="showThumbnails && processedImages.length > 1">
          <div class="thumbnail-scroll">
            <div
              v-for="(image, index) in processedImages"
              :key="index"
              class="thumbnail-item"
              :class="{
                'active': index === currentIndex,
                [image.type]: true
              }"
              @click="selectImage(index)"
            >
              <div class="thumbnail-image-wrapper">
                <img
                  :src="image.thumbnailUrl || image.url"
                  :alt="image.label"
                  @load="handleThumbnailLoad(index)"
                  @error="handleThumbnailError(index)"
                  :class="{ 'loaded': image.thumbLoaded }"
                />
                <div v-if="!image.thumbLoaded && !image.thumbError" class="thumbnail-loading">
                  <i class="pi pi-spinner pi-spin"></i>
                </div>
              </div>
              <div class="thumbnail-label">{{ image.label }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '@/utils/DebugLogger.ts';

const { t } = useI18n();

interface GalleryImage {
  id?: number | string;
  url: string;
  thumbnailUrl?: string;
  label?: string;
  type?: string;
  thumbLoaded?: boolean;
  thumbError?: boolean;
}

interface ThumbnailState {
  loaded: boolean;
  error: boolean;
}

// Props
const props = defineProps<{
  images: GalleryImage[];
  initialIndex?: number;
  loading?: boolean;
  emptyMessage?: string;
  showThumbnails?: boolean;
  showZoomControls?: boolean;
  showFullscreenControl?: boolean;
  showDeleteButton?: boolean;
}>();

// Emits
const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'delete', image: GalleryImage): void;
}>();

// Data
const imageLoading = ref(false);
const currentIndex = ref(props.initialIndex ?? 0);
const zoomLevel = ref(1);
const isFullscreen = ref(false);
const isDeleting = ref(false);
const thumbnailStates = ref(new Map<number, ThumbnailState>());

// Computed
const processedImages = computed(() => {
  return props.images.map((img, index) => {
    const state = thumbnailStates.value.get(index) || { loaded: false, error: false };
    return {
      ...img,
      label: img.label || `${t('Imagem')} ${index + 1}`,
      type: img.type || 'default',
      thumbLoaded: state.loaded,
      thumbError: state.error
    };
  });
});

const currentImage = computed((): GalleryImage | null => {
  if (processedImages.value.length === 0) return null;
  return processedImages.value[currentIndex.value] || processedImages.value[0];
});

// Watch for initial index changes
watch(() => props.initialIndex, (newIndex) => {
  currentIndex.value = newIndex ?? 0;
  imageLoading.value = true;
  resetZoom();
});

// Watch for images changes to reset thumbnail states
watch(() => props.images, () => {
  thumbnailStates.value.clear();
}, { deep: true });

// Methods
const handleClose = (): void => {
  if (isFullscreen.value) {
    exitFullscreen();
  }
  emit('close');
};

const previousImage = (): void => {
  if (currentIndex.value > 0) {
    currentIndex.value--;
    imageLoading.value = true;
    resetZoom();
  }
};

const nextImage = (): void => {
  if (currentIndex.value < processedImages.value.length - 1) {
    currentIndex.value++;
    imageLoading.value = true;
    resetZoom();
  }
};

const selectImage = (index: number): void => {
  currentIndex.value = index;
  imageLoading.value = true;
  resetZoom();
};

const zoomIn = (): void => {
  if (zoomLevel.value < 3) {
    zoomLevel.value = Math.min(zoomLevel.value + 0.25, 3);
  }
};

const zoomOut = (): void => {
  if (zoomLevel.value > 0.5) {
    zoomLevel.value = Math.max(zoomLevel.value - 0.25, 0.5);
  }
};

const resetZoom = (): void => {
  zoomLevel.value = 1;
};

const toggleFullscreen = (): void => {
  if (!isFullscreen.value) {
    enterFullscreen();
  } else {
    exitFullscreen();
  }
};

const enterFullscreen = (): void => {
  const elem = document.querySelector('.image-gallery') as any;
  if (elem) {
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
    }
  }
  isFullscreen.value = true;
};

const exitFullscreen = (): void => {
  const doc = document as any;
  if (doc.exitFullscreen) {
    doc.exitFullscreen();
  } else if (doc.webkitExitFullscreen) {
    doc.webkitExitFullscreen();
  } else if (doc.msExitFullscreen) {
    doc.msExitFullscreen();
  }
  isFullscreen.value = false;
};

const handleImageError = (event: Event): void => {
  imageLoading.value = false;
  const target = event.target as HTMLImageElement;
  log('ImageGallery', 'handleImageError', 'Error loading image', target.src);
  target.alt = t('Erro ao carregar imagem');
};

const handleThumbnailLoad = (index: number): void => {
  thumbnailStates.value.set(index, { loaded: true, error: false });
};

const handleThumbnailError = (index: number): void => {
  thumbnailStates.value.set(index, { loaded: false, error: true });
};

const handleDeleteCurrent = async (): Promise<void> => {
  if (!currentImage.value) return;

  const confirmMsg = t('Tem certeza que deseja remover esta imagem?');
  if (!confirm(confirmMsg)) return;

  isDeleting.value = true;
  try {
    emit('delete', currentImage.value);
    // Parent component handles the actual deletion and image list update
  } finally {
    isDeleting.value = false;
  }
};

const handleKeydown = (event: KeyboardEvent): void => {
  if (event.key === 'ArrowLeft') {
    previousImage();
  } else if (event.key === 'ArrowRight') {
    nextImage();
  } else if (event.key === 'Escape') {
    if (isFullscreen.value) {
      exitFullscreen();
    } else {
      handleClose();
    }
  } else if (event.key === '+' || event.key === '=') {
    if (props.showZoomControls) zoomIn();
  } else if (event.key === '-') {
    if (props.showZoomControls) zoomOut();
  } else if (event.key === '0') {
    if (props.showZoomControls) resetZoom();
  } else if (event.key === 'Delete' && props.showDeleteButton) {
    handleDeleteCurrent();
  }
};

const handleFullscreenChange = (): void => {
  const doc = document as any;
  if (!doc.fullscreenElement && !doc.webkitFullscreenElement && !doc.msFullscreenElement) {
    isFullscreen.value = false;
  }
};

// Lifecycle
onMounted(() => {
  log('ImageGallery', 'onMounted', 'Component mounted');
  document.addEventListener('keydown', handleKeydown);
  document.addEventListener('fullscreenchange', handleFullscreenChange);
  document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
  document.addEventListener('msfullscreenchange', handleFullscreenChange);
});

onUnmounted(() => {
  log('ImageGallery', 'onUnmounted', 'Component unmounted');
  document.removeEventListener('keydown', handleKeydown);
  document.removeEventListener('fullscreenchange', handleFullscreenChange);
  document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
  document.removeEventListener('msfullscreenchange', handleFullscreenChange);
});
</script>

<style scoped>
.gallery-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.95);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-gallery {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: #1a1a1a;
  color: #fff;
  position: relative;
}

.close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 100;
  background-color: rgba(0, 0, 0, 0.8);
  border: 2px solid #fff;
  color: #fff;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  font-size: 24px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background-color: #e74c3c;
  border-color: #e74c3c;
  transform: scale(1.1);
}

.loading-state,
.no-images {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 500px;
  color: #999;
}

.loading-state i,
.no-images i {
  margin-bottom: 20px;
}

.gallery-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* Main Image Container */
.main-image-container {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #000;
  overflow: hidden;
  min-height: 450px;
}

.image-info {
  position: absolute;
  top: 10px;
  left: 10px;
  right: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 10;
}

.image-label {
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 16px;
  background-color: rgba(52, 152, 219, 0.9);
}

.image-label.antes {
  background-color: rgba(52, 152, 219, 0.9);
}

.image-label.depois {
  background-color: rgba(46, 204, 113, 0.9);
}

.image-counter {
  padding: 8px 16px;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 4px;
  font-weight: 600;
}

.image-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.image-wrapper.fullscreen {
  padding: 60px 20px 20px;
}

.main-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: transform 0.2s ease, opacity 0.3s ease;
  cursor: grab;
  opacity: 0;
}

.main-image.image-loaded {
  opacity: 1;
}

.main-image:active {
  cursor: grabbing;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
}

/* Navigation Arrows */
.nav-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(0, 0, 0, 0.6);
  border: none;
  color: white;
  font-size: 30px;
  padding: 20px 15px;
  cursor: pointer;
  transition: background-color 0.2s;
  z-index: 10;
}

.nav-arrow:hover:not(:disabled) {
  background-color: rgba(0, 0, 0, 0.8);
}

.nav-arrow:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.nav-prev {
  left: 0;
}

.nav-next {
  right: 0;
}

/* Toolbar */
.toolbar {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  background-color: rgba(0, 0, 0, 0.8);
  padding: 10px;
  border-radius: 8px;
  z-index: 10;
}

.tool-btn {
  background-color: transparent;
  border: 1px solid #555;
  color: white;
  padding: 8px 12px;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s;
  font-size: 14px;
}

.tool-btn:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: #888;
}

.tool-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.tool-btn-danger:hover:not(:disabled) {
  background-color: rgba(231, 76, 60, 0.3);
  border-color: #e74c3c;
}

/* Thumbnail Strip */
.thumbnail-strip {
  background-color: #222;
  padding: 10px;
  border-top: 1px solid #333;
}

.thumbnail-scroll {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: 5px;
}

.thumbnail-scroll::-webkit-scrollbar {
  height: 8px;
}

.thumbnail-scroll::-webkit-scrollbar-track {
  background: #1a1a1a;
}

.thumbnail-scroll::-webkit-scrollbar-thumb {
  background: #555;
  border-radius: 4px;
}

.thumbnail-scroll::-webkit-scrollbar-thumb:hover {
  background: #777;
}

.thumbnail-item {
  flex-shrink: 0;
  width: 120px;
  cursor: pointer;
  border: 3px solid transparent;
  border-radius: 4px;
  overflow: hidden;
  transition: all 0.2s;
  position: relative;
}

.thumbnail-item:hover {
  border-color: #555;
}

.thumbnail-item.active {
  border-color: #3498db;
  box-shadow: 0 0 10px rgba(52, 152, 219, 0.5);
}

.thumbnail-item.antes.active {
  border-color: #3498db;
}

.thumbnail-item.depois.active {
  border-color: #2ecc71;
}

.thumbnail-image-wrapper {
  position: relative;
  width: 100%;
  height: 80px;
  background-color: #1a1a1a;
  display: flex;
  align-items: center;
  justify-content: center;
}

.thumbnail-item img {
  width: 100%;
  height: 80px;
  object-fit: cover;
  display: block;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.thumbnail-item img.loaded {
  opacity: 1;
}

.thumbnail-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #1a1a1a;
  color: #666;
}

.thumbnail-label {
  background-color: rgba(0, 0, 0, 0.8);
  padding: 4px 8px;
  font-size: 11px;
  text-align: center;
  font-weight: 600;
}

/* Responsive */
@media (max-width: 768px) {
  .main-image-container {
    min-height: 300px;
  }

  .nav-arrow {
    font-size: 24px;
    padding: 15px 10px;
  }

  .thumbnail-item {
    width: 80px;
  }

  .thumbnail-item img {
    height: 60px;
  }

  .toolbar {
    flex-wrap: wrap;
    bottom: 10px;
  }

  .tool-btn {
    padding: 6px 10px;
    font-size: 12px;
  }
}

.pi-spin {
  animation: pi-spin 1s infinite linear;
}

@keyframes pi-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(359deg);
  }
}
</style>
